import useAddressBook from '@/hooks/useAddressBook'
import { FEATURES } from '@/utils/chains'
import { useMemo } from 'react'
import useAsync from '@/hooks/useAsync'
import useDebounce from './useDebounce'
import { useHasFeature } from './useChains'
import useChainId from './useChainId'
import { mirrorNodeFetch } from '@/services/mirrorNodeCache'

interface AccountResponse {
  account: string
  evm_address: string
}

export const useAddressResolver = (address: string, forceResolve: boolean = false) => {
  const addressBook = useAddressBook()
  const chainId = useChainId()
  const debouncedValue = useDebounce(address, 200)
  const addressBookName = addressBook[address]
  const isDomainLookupEnabled = useHasFeature(FEATURES.DOMAIN_LOOKUP)
  const shouldResolve = (!addressBookName || forceResolve) && isDomainLookupEnabled && !!debouncedValue

  const [ens, _, isResolving] = useAsync<string | undefined>(() => {
    if (!shouldResolve) return
    return mirrorNodeFetch<AccountResponse>(`/api/v1/accounts/${debouncedValue}`, chainId).then((data) => data?.account)
  }, [debouncedValue, chainId, shouldResolve])

  const resolving = shouldResolve && isResolving

  return useMemo(
    () => ({
      ens,
      name: addressBookName,
      resolving,
    }),
    [ens, addressBookName, resolving],
  )
}
